import type { CustomAttributeListOutput } from "@orsane/schema/tasks";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { useCreateCustomAttributeMutation, useDeleteCustomAttributeMutation, useUpdateCustomAttributeMutation } from "@/mutations/mutations";
import { customAttributesQueryOptions } from "@/queries/queries";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import { AttributeDialog } from "./AttributeDialog";
import { createAttributesColumns } from "./attributes-columns";

export default function AttributesSettings() {
    const deleteAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const { data: attributes = [] } = useQuery(customAttributesQueryOptions());
    const { mutate: createCustomAttribute, isPending: isCreating } = useCreateCustomAttributeMutation();
    const { mutate: updateCustomAttribute, isPending: isUpdating } = useUpdateCustomAttributeMutation();
    const { mutate: deleteCustomAttribute } = useDeleteCustomAttributeMutation();

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedAttribute, setSelectedAttribute] = useState<CustomAttributeListOutput | undefined>();

    const handleEdit = (attribute: CustomAttributeListOutput) => {
        setSelectedAttribute(attribute);
        setEditDialogOpen(true);
    };

    const handleDelete = async (id: number) => {
        const confirmed = await deleteAlertDialogRef.current!.show();
        if (confirmed) {
            deleteCustomAttribute(
                { id: id },
                {
                    onSuccess: () => {
                        toast.success("Deleted", { description: "Custom attribute deleted successfully" });
                    },
                }
            );
        }
    };

    const handleCreateSubmit = (data: any) => {
        createCustomAttribute(data, {
            onSuccess: () => {
                toast.success("Created", { description: "Custom attribute created successfully" });
                setCreateDialogOpen(false);
            },
        });
    };

    const handleUpdateSubmit = (data: any) => {
        updateCustomAttribute(data, {
            onSuccess: () => {
                toast.success("Updated", { description: "Custom attribute updated successfully" });
                setEditDialogOpen(false);
                setSelectedAttribute(undefined);
            },
        });
    };

    const columns = createAttributesColumns(handleEdit, handleDelete);

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Attribute
                </Button>
            </div>

            <DataTable emptyMessage="No custom attributes yet. Add one to get started." columns={columns} data={attributes} />

            <AttributeDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} onSubmit={handleCreateSubmit} isLoading={isCreating} />

            <AttributeDialog
                open={editDialogOpen}
                onOpenChange={(open) => {
                    setEditDialogOpen(open);
                    if (!open) setSelectedAttribute(undefined);
                }}
                attribute={selectedAttribute}
                onSubmit={handleUpdateSubmit}
                isLoading={isUpdating}
            />

            <DefaultAlertDialog
                ref={deleteAlertDialogRef}
                type={AlertDialogType.Delete}
                title="Remove custom attribute"
                description="Are you sure you want to remove this custom attribute from the team? Every list using this attribute will be updated to remove it."
            />
        </div>
    );
}
